// Enhanced Mobile Responsive Wallet.js — Glassmorphism Light Theme
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import moment from 'moment-timezone';
import {
  getWalletBalance,
  getUserTransactions
} from '../actions/walletActions'
import '../assests/glass-theme.css'
import { displayTransactionId } from '../utils/transactionIdUtils'

const Wallet = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userDetails, setUserDetails] = useState(null);

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  // Wallet balance state
  const getWalletBalanceState = useSelector((state) => state.getWalletBalance)
  const { loadingGetWalletBalanceInfo, getWalletBalanceData, errorGetWalletBalanceInfo } = getWalletBalanceState

  // User transactions state
  const getUserTransactionsState = useSelector((state) => state.getUserTransactions)
  const { loadingGetUserTransactionsInfo, getUserTransactionsData, errorGetUserTransactionsInfo } = getUserTransactionsState
  const [activeQuickFilter, setActiveQuickFilter] = useState('');

  // Extract data
  const balance = getWalletBalanceData?.balance || 0;
  const transactions = getUserTransactionsData?.transactions || [];
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    transactionType: ''
  });
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  const handleViewAllTransactions = () => {
    setShowTransactionModal(true);
    // Reset filters and show all transactions
    setFilters({
      fromDate: '',
      toDate: '',
      transactionType: ''
    });
    setFilteredTransactions(transactions);
  };

  const handleQuickFilter = (days) => {
    const toDate = moment().tz('Asia/Kolkata').endOf('day');
    const fromDate = moment().tz('Asia/Kolkata').subtract(days, 'days').startOf('day');

    setFilters({
      ...filters,
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD')
    });

    // Set active quick filter
    setActiveQuickFilter(`${days}days`);

    applyFilters(fromDate, toDate, filters.transactionType);
  };

  const applyFilters = (fromDate, toDate, type) => {
    let filtered = [...transactions];

    if (fromDate) {
      const fromMoment = moment(fromDate).tz('Asia/Kolkata').startOf('day');
      filtered = filtered.filter(t =>
        moment(t.createdAt).tz('Asia/Kolkata').isSameOrAfter(fromMoment)
      );
    }
    if (toDate) {
      const toMoment = moment(toDate).tz('Asia/Kolkata').endOf('day');
      filtered = filtered.filter(t =>
        moment(t.createdAt).tz('Asia/Kolkata').isSameOrBefore(toMoment)
      );
    }
    if (type) {
      filtered = filtered.filter(t => t.transactionType === type);
    }

    setFilteredTransactions(filtered);
  };

  const handleFilterSubmit = () => {
    const fromDate = filters.fromDate ? moment(filters.fromDate).tz('Asia/Kolkata') : null;
    const toDate = filters.toDate ? moment(filters.toDate).tz('Asia/Kolkata') : null;
    applyFilters(fromDate, toDate, filters.transactionType);
  };

  const handleTypeFilter = (type) => {
    setFilters({ ...filters, transactionType: type });
    applyFilters(
      filters.fromDate ? moment(filters.fromDate).tz('Asia/Kolkata') : null,
      filters.toDate ? moment(filters.toDate).tz('Asia/Kolkata') : null,
      type
    );
    // Don't reset activeQuickFilter here
  };

  const clearFilters = () => {
    setFilters({ fromDate: '', toDate: '', transactionType: '' });
    setActiveQuickFilter(''); // Reset active quick filter
    setFilteredTransactions(transactions);
  };

  useEffect(() => {
    // Get user info from localStorage or userInfo
    const storedUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const currentUser = userInfo || storedUserInfo;

    if (!currentUser || !currentUser.id) {
      navigate('/login')
    } else {
      setUserDetails(currentUser);
      // Fetch wallet data
      dispatch(getWalletBalance());
      dispatch(getUserTransactions(1, 10));
    }
  }, [userInfo, navigate, dispatch])

  useEffect(() => {
    if (userDetails) {
      // Refresh data every 30 seconds
      const interval = setInterval(() => {
        dispatch(getWalletBalance());
        dispatch(getUserTransactions(1, 10));
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [userDetails, dispatch]);

  // Handle errors
  useEffect(() => {
    if (errorGetWalletBalanceInfo) {
      toast.error(errorGetWalletBalanceInfo);
    }
    if (errorGetUserTransactionsInfo) {
      toast.error(errorGetUserTransactionsInfo);
    }
  }, [errorGetWalletBalanceInfo, errorGetUserTransactionsInfo])

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      setFilteredTransactions(transactions);
    }
  }, [transactions]);

  // Dummy button handlers
  const handleDummyDeposit = () => {
    toast.info('Top-up feature is currently disabled');
  };

  // Display label for a wallet transaction type ("deposit" is shown as "Top-up")
  const formatTransactionType = (type = '') =>
    (type.charAt(0).toUpperCase() + type.slice(1)).replace(/deposit/i, (m) => (m === 'Deposit' ? 'Top-up' : 'top-up'));

  const handleDummyWithdraw = () => {
    toast.info('Withdrawal feature is currently disabled');
  };

  const handleDummyBuyIn = () => {
    toast.info('Buy-in feature is currently disabled');
  };

  const handleDummyCashout = () => {
    toast.info('Cashout feature is currently disabled');
  };

  if (!userDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-cm-gold border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  const walletUserName = userDetails?.name || 'Player'
  const walletInitials = walletUserName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  return (
    <div className="bg-scene-light" style={{ minHeight: '100vh', overflowX: 'hidden' }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={walletStyles.header}>
        <button onClick={() => navigate('/dashboard')} style={walletStyles.backBtn} aria-label="Back">
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={walletStyles.headerTitle}>My Wallet</h1>
        <button
          onClick={() => { dispatch(getWalletBalance()); dispatch(getUserTransactions(1, 10)); toast.success('Refreshed!'); }}
          style={walletStyles.refreshBtn}
          aria-label="Refresh"
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* ── Main Content ───────────────────────────────────────── */}
      <div style={walletStyles.contentArea}>

        {/* Balance Card — light glass */}
        <div className="glass-light gl-anim-up" style={walletStyles.balanceCard}>
          <p style={walletStyles.balLabel}>Available Balance</p>
          {loadingGetWalletBalanceInfo ? (
            <div style={walletStyles.balSkeleton} />
          ) : (
            <h2 style={walletStyles.balAmount}>₹ {balance.toLocaleString('en-IN')}</h2>
          )}

          <div style={walletStyles.balFooter}>
            <div>
              <p style={walletStyles.balName}>{walletUserName}</p>
              <p style={walletStyles.balId}>{userDetails?.identifier || ''}</p>
            </div>
            <div className="avatar-circle" style={{ background: 'var(--cm-gold-soft)', color: 'var(--cm-text)', fontSize: 'clamp(13px, 1.8vw, 14px)', width: 'clamp(38px, 6vw, 42px)', height: 'clamp(38px, 6vw, 42px)' }}>
              {walletInitials}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={walletStyles.actionGrid} className="gl-anim-up gl-delay-1">
          {[
            { label: 'TOP-UP',   fn: handleDummyDeposit  },
            { label: 'WITHDRAW', fn: handleDummyWithdraw },
            { label: 'BUY-IN',   fn: handleDummyBuyIn   },
            { label: 'CASHOUT',  fn: handleDummyCashout  },
          ].map(({ label, fn }) => (
            <button key={label} onClick={fn} style={walletStyles.actionBtn}>
              {label}
            </button>
          ))}
        </div>

        {/* Transactions Card */}
        <div className="glass-light gl-anim-up gl-delay-2" style={walletStyles.txnCard}>
          <div style={walletStyles.txnHeader}>
            <h3 style={walletStyles.txnTitle}>Recent Transactions</h3>
            <button onClick={handleViewAllTransactions} style={walletStyles.viewAllBtn}>View All</button>
          </div>

          {loadingGetUserTransactionsInfo ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={walletStyles.skeleton} />
              ))}
            </div>
          ) : transactions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {transactions.slice(0, 3).map((txn) => (
                <div key={txn._id} style={walletStyles.txnRow}>
                  <div style={walletStyles.txnRowLeft}>
                    <p style={walletStyles.txnDate}>
                      {new Date(txn.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · {new Date(txn.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p style={walletStyles.txnType}>
                      {formatTransactionType(txn.transactionType)}
                    </p>
                    <p style={walletStyles.txnId}>{displayTransactionId(txn)}</p>
                  </div>
                    <div style={walletStyles.txnRowRight}>
                    <p style={{ ...walletStyles.txnStatus, color: txn.status === 'approved' ? 'var(--cm-success)' : txn.status === 'pending' ? 'var(--cm-warning)' : 'var(--cm-danger)' }}>
                      {txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                    </p>
                    {txn.bonusAmount > 0 ? (
                      <>
                        <p style={{ ...walletStyles.txnAmount, color: ['deposit', 'chip-deposit'].includes(txn.transactionType) ? 'var(--cm-success)' : 'var(--cm-text)' }}>
                          {['buy-in', 'cashout', 'table-charge', 'food-drink', 'membership', 'event-entry'].includes(txn.transactionType) ? '-' : ['deposit-buyin', 'chip-cashout'].includes(txn.transactionType) ? '' : '+'}₹{(txn.amount + txn.bonusAmount).toLocaleString('en-IN')}
                        </p>
                        <p style={walletStyles.bonusTag}>+₹{txn.bonusAmount.toLocaleString('en-IN')} bonus</p>
                      </>
                    ) : (
                      <p style={{ ...walletStyles.txnAmount, color: ['deposit', 'chip-deposit'].includes(txn.transactionType) ? 'var(--cm-success)' : 'var(--cm-text)' }}>
                        {['buy-in', 'cashout', 'table-charge', 'food-drink', 'membership', 'event-entry'].includes(txn.transactionType) ? '-' : ['deposit-buyin', 'chip-cashout'].includes(txn.transactionType) ? '' : '+'}₹{txn.amount.toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <svg style={{ margin: '0 auto 12px', display: 'block', color: 'var(--cm-text-muted)' }} width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p style={walletStyles.emptyTxt}>No transactions found</p>
            </div>
          )}

          <button onClick={handleViewAllTransactions} className="btn-gold" style={{ marginTop: 20, width: '100%' }}>
            VIEW ALL TRANSACTIONS
          </button>
        </div>
      </div>

      {/* ── Transaction Modal ───────────────────────────────────── */}
      {showTransactionModal && (
        <div className="bg-scene-light" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          {/* Modal Header */}
          <div style={{ ...walletStyles.header, position: 'sticky', top: 0, zIndex: 10 }}>
            <button onClick={() => setShowTransactionModal(false)} style={walletStyles.backBtn}>
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 style={walletStyles.headerTitle}>All Transactions</h3>
            <div style={{ width: 40 }} />
          </div>

          {/* Active filters strip */}
          {(filters.fromDate || filters.toDate || filters.transactionType) && (
            <div style={{ padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {filters.fromDate && <span style={walletStyles.filterChip}>From: {filters.fromDate}</span>}
              {filters.toDate   && <span style={walletStyles.filterChip}>To: {filters.toDate}</span>}
              {filters.transactionType && <span style={walletStyles.filterChip}>{formatTransactionType(filters.transactionType)}</span>}
              <button onClick={clearFilters} style={{ ...walletStyles.filterChip, background: 'var(--cm-danger-soft)', color: 'var(--cm-danger)', border: '1px solid var(--cm-danger)' }}>Clear</button>
            </div>
          )}

          {/* Scrollable list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 100px' }}>
            {filteredTransactions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredTransactions.map((txn) => (
                  <div key={txn._id} className="glass-light" style={walletStyles.txnRow}>
                    <div style={walletStyles.txnRowLeft}>
                      <p style={walletStyles.txnDate}>{moment(txn.createdAt).tz('Asia/Kolkata').format('DD MMM YYYY · hh:mm a')}</p>
                      <p style={walletStyles.txnType}>{formatTransactionType(txn.transactionType)}</p>
                      <p style={walletStyles.txnId}>{displayTransactionId(txn)}</p>
                    </div>
                    <div style={walletStyles.txnRowRight}>
                      <p style={{ ...walletStyles.txnStatus, color: txn.status === 'approved' ? 'var(--cm-success)' : txn.status === 'pending' ? 'var(--cm-warning)' : 'var(--cm-danger)' }}>
                        {txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                      </p>
                      {txn.bonusAmount > 0 ? (
                        <>
                          <p style={{ ...walletStyles.txnAmount, color: ['deposit', 'chip-deposit'].includes(txn.transactionType) ? 'var(--cm-success)' : 'var(--cm-text)' }}>
                            {['buy-in', 'cashout', 'table-charge', 'food-drink', 'membership', 'event-entry'].includes(txn.transactionType) ? '-' : ['deposit-buyin', 'chip-cashout'].includes(txn.transactionType) ? '' : '+'}₹{(txn.amount + txn.bonusAmount).toLocaleString('en-IN')}
                          </p>
                          <p style={walletStyles.bonusTag}>+₹{txn.bonusAmount.toLocaleString('en-IN')} bonus</p>
                        </>
                      ) : (
                        <p style={{ ...walletStyles.txnAmount, color: ['deposit', 'chip-deposit'].includes(txn.transactionType) ? 'var(--cm-success)' : 'var(--cm-text)' }}>
                          {['buy-in', 'cashout', 'table-charge', 'food-drink', 'membership', 'event-entry'].includes(txn.transactionType) ? '-' : ['deposit-buyin', 'chip-cashout'].includes(txn.transactionType) ? '' : '+'}₹{txn.amount.toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', paddingTop: 60 }}>
                <p style={walletStyles.emptyTxt}>No transactions found for selected filters</p>
              </div>
            )}
          </div>

          {/* Filter button */}
          <div style={{ padding: '12px 16px', paddingBottom: 'max(env(safe-area-inset-bottom),12px)', background: 'var(--cm-surface)',  borderTop: '1px solid var(--cm-border)' }}>
            <button onClick={() => setShowFilterModal(true)} className="btn-gold">FILTER</button>
          </div>
        </div>
      )}

      {/* ── Filter Modal ──────────────────────────────────────────── */}
      {showFilterModal && (
        <div className="bg-scene-light" style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...walletStyles.header, position: 'sticky', top: 0 }}>
            <button onClick={() => setShowFilterModal(false)} style={walletStyles.backBtn}>
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 style={walletStyles.headerTitle}>Filter Transactions</h3>
            <div style={{ width: 40 }} />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 100px' }}>
            {/* Date range */}
            <p style={walletStyles.filterSectionLabel}>Date Range</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <input type="date" value={filters.fromDate}
                onChange={e => { setFilters({ ...filters, fromDate: e.target.value }); setActiveQuickFilter(''); }}
                style={walletStyles.dateInput} />
              <input type="date" value={filters.toDate}
                onChange={e => { setFilters({ ...filters, toDate: e.target.value }); setActiveQuickFilter(''); }}
                style={walletStyles.dateInput} />
            </div>

            {/* Quick selections */}
            <p style={walletStyles.filterSectionLabel}>Quick Selections</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
              {[{l:'7 days',v:'7days',d:7},{l:'15 days',v:'15days',d:15},{l:'Last Month',v:'30days',d:30,full:true}].map(q=>(
                <button key={q.v} onClick={() => handleQuickFilter(q.d)}
                  style={{ ...walletStyles.filterBtn, ...(activeQuickFilter===q.v ? walletStyles.filterBtnActive : {}), ...(q.full ? { gridColumn: '1/-1' } : {}) }}>
                  {q.l}
                </button>
              ))}
            </div>

            {/* Transaction type */}
            <p style={walletStyles.filterSectionLabel}>Transaction Type</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {['deposit'].map(t=>(
                <button key={t} onClick={() => handleTypeFilter(t)}
                  style={{ ...walletStyles.filterBtn, ...(filters.transactionType===t ? walletStyles.filterBtnActive : {}) }}>
                  {formatTransactionType(t)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '12px 16px', paddingBottom: 'max(env(safe-area-inset-bottom),12px)', background: 'var(--cm-surface)',  borderTop: '1px solid var(--cm-border)' }}>
            <button onClick={() => { handleFilterSubmit(); setShowFilterModal(false); }} className="btn-gold">APPLY FILTER</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Wallet Styles ───────────────────────────────────────────────
const walletStyles = {
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: 'max(env(safe-area-inset-top), 10px) 16px 14px', background: 'var(--cm-surface)',
    
    borderBottom: '1px solid var(--cm-border)',
  },
  headerTitle: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 'clamp(17px, 2.2vw, 20px)', fontWeight: 700, color: 'var(--cm-text)',
  },
  backBtn: {
    width: 40, height: 40, borderRadius: '50%', border: 'none',
    background: 'var(--cm-surface)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cm-text)',
  },
  refreshBtn: {
    width: 40, height: 40, borderRadius: '50%', border: 'none',
    background: 'var(--cm-surface)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cm-text)',
  },
  contentArea: {
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    padding: 'clamp(12px, 3vw, 24px)',
    paddingBottom: 'max(40px, env(safe-area-inset-bottom))',
    boxSizing: 'border-box',
  },
  balanceCard: { padding: 'clamp(18px, 3.2vw, 24px) clamp(16px, 3vw, 22px)', marginBottom: 16 },
  balLabel: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 'clamp(12px, 1.6vw, 13px)', fontWeight: 500, color: 'var(--cm-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8,
  },
  balAmount: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 800, color: 'var(--cm-text)', lineHeight: 1.1, marginBottom: 18,
  },
  balSkeleton: {
    height: 40, width: 180, background: 'var(--cm-surface-2)', borderRadius: 8, marginBottom: 20,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  balFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
  },
  balName: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 'clamp(14px, 2vw, 16px)', fontWeight: 700, color: 'var(--cm-text)', lineHeight: 1.3, overflowWrap: 'anywhere',
  },
  balId: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 'clamp(11px, 1.6vw, 12px)', color: 'var(--cm-text-muted)', marginTop: 2, letterSpacing: '0.4px', overflowWrap: 'anywhere',
  },
  actionGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16,
  },
  actionBtn: {
    padding: '14px 10px',
    background: 'var(--cm-surface)', 
    border: '1px solid var(--cm-border)', borderRadius: 50,
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 13, fontWeight: 700, color: 'var(--cm-text)', letterSpacing: '1px',
    cursor: 'pointer',
  },
  txnCard: { padding: 'clamp(16px, 2.6vw, 22px) clamp(14px, 2.4vw, 18px)' },
  txnHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  txnTitle: { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 'clamp(16px, 2vw, 18px)', fontWeight: 700, color: 'var(--cm-text)' },
  viewAllBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 13, fontWeight: 600, color: 'var(--cm-gold-text)',
  },
  skeleton: {
    height: 60, borderRadius: 12, background: 'var(--cm-surface-2)',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  txnRow: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '14px 16px', borderRadius: 14,
  },
  txnRowLeft: { flex: '1 1 240px', minWidth: 0 },
  txnRowRight: { textAlign: 'right', flex: '0 1 auto', marginLeft: 'auto', minWidth: 'fit-content' },
  txnDate: { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 11, color: 'var(--cm-text-muted)', marginBottom: 3, lineHeight: 1.35, overflowWrap: 'anywhere' },
  txnType: { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 14, fontWeight: 600, color: 'var(--cm-text)', marginBottom: 2, lineHeight: 1.35, overflowWrap: 'anywhere' },
  txnId:   { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 11, color: 'var(--cm-text-muted)', lineHeight: 1.35, overflowWrap: 'anywhere', wordBreak: 'break-word' },
  txnStatus: { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 11, fontWeight: 700, marginBottom: 4, whiteSpace: 'nowrap', background: 'var(--cm-surface)', borderRadius: 999, padding: '4px 10px', display: 'inline-block' },
  txnAmount: { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 'clamp(15px, 2vw, 17px)', fontWeight: 800, color: 'var(--cm-text)', whiteSpace: 'nowrap' },
  emptyTxt: { fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 14, color: 'var(--cm-text-muted)' },
  bonusTag: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 10, fontWeight: 700, color: 'var(--cm-gold-text)',
    background: 'var(--cm-gold-soft)', borderRadius: 6,
    padding: '2px 6px', marginTop: 3, display: 'inline-block',
    letterSpacing: '0.3px',
  },
  filterChip: {
    padding: '5px 12px', borderRadius: 20,
    background: 'var(--cm-gold-soft)', color: 'var(--cm-gold-text)',
    border: '1px solid var(--cm-border)',
    fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 12, fontWeight: 500,
  },
  filterSectionLabel: {
    fontFamily: 'var(--font-primary, Poppins, sans-serif)',
    fontSize: 13, fontWeight: 700, color: 'var(--cm-text)',
    textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10,
  },
  dateInput: {
    width: '100%', padding: '14px 16px', borderRadius: 14,
    border: '1px solid var(--cm-border)',
    background: 'var(--cm-surface)', 
    fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 15, color: 'var(--cm-text)',
    outline: 'none', boxSizing: 'border-box',
  },
  filterBtn: {
    padding: '13px 10px', borderRadius: 14,
    background: 'var(--cm-surface)', 
    border: '1px solid var(--cm-border)',
    fontFamily: 'var(--font-primary, Poppins, sans-serif)', fontSize: 14, fontWeight: 600, color: 'var(--cm-text)',
    cursor: 'pointer',
  },
  filterBtnActive: {
    background: 'var(--cm-gold-soft)',
    border: 'none', color: 'var(--cm-text)',
    boxShadow: 'none',
  },
}

export default Wallet
